import React from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, NoticeBox, Tags } from "../../ui";
import useBranchApplications from "./useBranchApplications";
import { BRANCH_TAG, REQUEST_TAG, REQUEST_TYPE_TAG } from "./applicationsModel";

// "Manage Applications" of one branch: every supported application with this
// branch's status, and the same actions as the Applications page (subscribe,
// integrate / edit information, activate, deactivate, requests). The actions
// call the shared workflow (`flows`), pre-selecting this branch; other
// branches of the store can be added inside each flow.
export default function BranchApplicationsManager({ open, branch, storeId, flows, onClose }) {
  const { t, canManage, token, revision, ar } = flows;
  const view = useBranchApplications({ token, storeId, branchId: branch?.id, revision, enabled: open, t });
  if (!open || !branch) return null;

  const start = (type, app, extra = {}) => flows.open(type, app, { branchIds: [branch.id], ...extra });
  const applications = view.data?.applications || [];

  return (
    <LudDashboardModal open title={t.branchManager.title(branch.name)} size="lg" onClose={onClose} footer={<Button variant="neutral" appearance="soft" onClick={onClose}>{t.close}</Button>}>
      <div className="lud-app-stack">
        <p className="lud-app-hint">{t.branchManager.hint}</p>
        {!canManage && <small className="lud-app-hint">{t.branchManager.readOnly}</small>}
        {view.status === "loading" && <p className="lud-app-lead" role="status">{t.loading}</p>}
        {view.status === "error" && (
          <NoticeBox variant="danger" size="sm">
            {t.branchManager.loadError}: {view.error}{" "}
            <Button size="sm" variant="neutral" appearance="soft" onClick={view.reload}>{t.retry}</Button>
          </NoticeBox>
        )}
        <ul className="lud-app-request-list lud-app-branch-apps">
          {applications.map((app) => {
            const b = app.branch;
            const openRequests = app.requests.filter((request) => request.status === "inprogress");
            const subscription = b.subscription;
            const canSubscribe = b.integration === "unconfigured" && !subscription && b.eligibility.eligible;
            return (
              <li key={app.key} className="lud-app-request" data-app={app.key}>
                <div className="lud-app-request-head">
                  <strong>{ar ? app.nameAr : app.nameEn}</strong>
                  <span className="lud-app-tags">
                    <Tags variant={BRANCH_TAG[b.integration] || "neutral"} appearance="soft" size="sm">{t.status[b.integration] || b.integration}</Tags>
                    {b.integration === "active" && !b.effective && <Tags variant="danger" appearance="soft" size="sm">{t.status.closed}</Tags>}
                    {subscription && subscription.status !== "completed" && (
                      <Tags variant={REQUEST_TAG[subscription.status] || "neutral"} appearance="soft" size="sm">{`${t.branchManager.subscription}: ${t.status[subscription.status] || subscription.status}`}</Tags>
                    )}
                    {openRequests.map((request) => (
                      <Tags key={request.id} variant={REQUEST_TYPE_TAG[request.type]} appearance="soft" size="sm" title={t.branchManager.pendingRequest}>
                        {`${t.requestTypes[request.type]} · ${t.changeStatus[request.status]}`}
                      </Tags>
                    ))}
                  </span>
                </div>
                {(b.appId || b.appName) && <small dir="auto">{[b.appId, b.appName].filter(Boolean).join(" · ")}</small>}
                {b.integration === "unconfigured" && !subscription && !b.eligibility.eligible && (
                  <small className="lud-app-reason">{b.eligibility.missing.map((field) => t.missingFields[field] || field).join(" · ")}</small>
                )}
                {app.requests.find((request) => request.status === "rejected") && b.integration !== "active" && !openRequests.length && (
                  <small className="lud-app-error">{`${t.changes.rejection}: ${app.requests.find((request) => request.status === "rejected").rejectionReason || "—"}`}</small>
                )}
                <div className="lud-app-actions">
                  {canManage && canSubscribe && <Button size="sm" variant="primary" appearance="soft" onClick={() => start("subscribe", app)}>{t.branchManager.subscribe}</Button>}
                  {canManage && !b.pendingRequest && (
                    <Button size="sm" variant="primary" appearance="soft" onClick={() => start("integrate", app)}>
                      {b.integration === "unconfigured" ? t.branchManager.integrate : t.branchManager.editInfo}
                    </Button>
                  )}
                  {canManage && b.activationEligible && <Button size="sm" variant="success" appearance="soft" onClick={() => start("activateBranches", app)}>{t.branchManager.activate}</Button>}
                  {canManage && b.deactivationEligible && <Button size="sm" variant="danger" appearance="soft" onClick={() => start("deactivate", app)}>{t.branchManager.deactivate}</Button>}
                  <Button size="sm" variant="neutral" appearance="soft" onClick={() => flows.open("manage", app)}>{t.branchManager.requests}</Button>
                </div>
                {openRequests.length > 0 && <small className="lud-app-hint" dir="ltr">{openRequests.map((request) => request.reference).join(" · ")}</small>}
              </li>
            );
          })}
        </ul>
      </div>
    </LudDashboardModal>
  );
}
